/**
 * Ancestor-filter pushdown for the enriched / flattened virtual views — the
 * shared, factory-agnostic seam both `createEnrichedView` and
 * `createFlattenedView` use to push an agent's ancestor-scoping predicate
 * (e.g. `(in (col % field externalId) ["field:STORM_EYE"])`) down to CDF
 * instead of full-scanning the source and replaying the filter client-side
 * over a server-truncated read.
 *
 * Two phases:
 *
 *   1. PLAN time (pure, sync) — `buildPushdownHooks` produces the
 *      `PipelineHooks` `parseSourcePipeline` folds: it recognises ancestor
 *      scope leaves, delegates pure source-native predicates to the real CDF
 *      recognizer (`isPushableNode`), and splits a pushed subtree into the
 *      source-native remainder (`filter`) plus the ancestor scopes
 *      (`ancestorScopes`). Ancestor scopes ride the native blob as data, NOT
 *      a translated filter — they need a fetch-time reverse lookup the planner
 *      has no client for.
 *
 *   2. FETCH time (async) — `resolveAncestorScopeFilter` turns each scope into
 *      a source-space `in` predicate: directly on the source when the relation
 *      is source-resident (Hierarchy/SectorAllocation), else via a semi-join
 *      through FlatHierarchy keyed on the producing-string ref. The result is
 *      AND-ed into the source query before `buildCdfViewQuery`.
 *
 * SSOT for the pushdown shared by both custom-fetch leaf factories — never
 * re-derive the recognizer / rename / reverse-lookup in a factory.
 */

import type { CogniteClient, NodeDefinition } from '@cognite/sdk';

import { CDF_IDENTITY_TABLE } from '#core/lib/cognite/identity.ts';
import type { DataModelView, MintSeriesSource } from '#core/lib/cognite/view.ts';
import { instancesToColumns, SET_REF_MAX, viewToSchema } from '#core/lib/cognite/view.ts';
import { NO_POS } from '#core/lib/expr/ast.ts';
import type { SourceFetcherCtx } from '#core/lib/expr/EvalContext.ts';
import { SetRefTooLargeError } from '#core/lib/expr/errors.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import { andChildren, astShape, embeddedStringVector, isAndCall } from '#core/lib/expr/normalize.ts';
import { isPushableNode } from '#core/lib/expr/ops/knowledge-graph/pushdown.ts';
import { buildCdfViewQuery, type CdfViewNative } from '#core/lib/expr/providers/cdf-view.ts';
import { rowColLabels } from '#core/lib/expr/providers/decode.ts';
import { type PipelineHooks, parseSourcePipeline } from '#core/lib/expr/providers/pipeline.ts';
import type { DatasetProvider, DecodedParams, PlanCtx, ProviderSource } from '#core/lib/expr/providers/types.ts';
import { type ColumnDoc, projectSchema, type Schema, type ViewDescription } from '#core/lib/expr/schema/types.ts';
import type { Cell, MaterializedDataset } from '#core/lib/expr/value.ts';

import { colRef, op } from './ast.ts';
import { makeVirtualViewProviderShell, type VirtualView } from './createReshapedView.ts';
import { type AncestorRelation, ancestorStruct, asRef, STRING_REL_COLUMN } from './enrichAncestors.ts';
import { andClauses, andFilters, renameCanonicalToSource } from './predicateRewrite.ts';
import type { Resolved, ResolvedFlat } from './shapes/index.ts';
import { FlatHierarchyAgentView } from '#schema/generated/ipso/views.ts';

/**
 * A resolved ancestor scope: the hierarchy relation being filtered and the
 * literal externalIds it is scoped to. Collected at plan time, applied at
 * fetch time.
 */
export type AncestorScope = {
  readonly relation: AncestorRelation;
  readonly externalIds: readonly string[];
};

/** The plan-time `enrich`/`from` shape both factories thread to the pushdown. */
export type ScopeEnrich = {
  /** `'self'` (entity-grain, row identity is the driving ref) or `'column'`. */
  readonly from?: 'column' | 'self';
  /** Canonical column carrying the producing-string ref (column-grain). */
  readonly stringRefColumn?: string;
  /**
   * The FlatHierarchy column the view's own rows match when `from: 'self'` — the
   * producing-string column by default, or a coarser ancestor relation (e.g.
   * `well`) for an entity view at that grain. The semi-join reverse-resolves an
   * ancestor scope to a set of refs at THIS grain.
   */
  readonly selfColumn?: string;
};

/**
 * The externalId of an inlined relation-membership identity node, or
 * `undefined` when the node is not a claimed row identity. Identity-ness is the
 * TYPE-layer question — answered via `CDF_IDENTITY_TABLE.claimingIdentity` over
 * the node's self-evaluating value — so this generic pushdown code never sniffs
 * the `{space, externalId}` struct itself. The externalId is decoded through
 * the module's canonical `asRef` seam; the ancestor-scope push resolves by
 * externalId (`resolveAncestorScopeFilter`), so the refs' spaces are not used.
 */
const identityExternalId = (node: AstInput): string | undefined => {
  const shape = astShape(node);
  if (shape.kind !== 'embedded' || CDF_IDENTITY_TABLE.claimingIdentity(shape.value) === undefined) {
    return undefined;
  }
  const ref = asRef(shape.value as Cell);
  return ref === null ? undefined : ref.externalId;
};

/**
 * Collect the externalIds a relation-membership RHS scopes to: literal string
 * ids, or — after the set-refs pre-pass inlines a relation-membership
 * `(col ent externalId)` to full {space, externalId} identities — the
 * externalId of each claimed instance identity. Returns `undefined` when the
 * RHS is neither a literal-string shape nor a (vector of) claimed identities.
 */
const membershipExternalIds = (node: AstInput): readonly string[] | undefined => {
  const strings = embeddedStringVector(node);
  if (strings !== undefined) {
    return strings;
  }
  const single = identityExternalId(node);
  if (single !== undefined) {
    return [single];
  }
  const shape = astShape(node);
  if (shape.kind === 'vector' && shape.elements.length > 0) {
    const ids = shape.elements.flatMap((el: AstInput): string[] => {
      const id = identityExternalId(el);
      return id === undefined ? [] : [id];
    });
    return ids.length === shape.elements.length ? ids : undefined;
  }
  return undefined;
};

/**
 * Classify `node` as an ancestor-scope leaf: an `(= …)` or `(in …)` whose LHS
 * `rowColLabels` is `[rel, 'externalId']` with `rel ∈ relations` and whose RHS
 * scopes to a set of externalIds — literal strings or the set-refs-inlined
 * instance refs (see {@link membershipExternalIds}). Returns the scope, else
 * undefined.
 */
export const isAncestorScopeLeaf = (
  node: AstInput,
  relations: readonly AncestorRelation[],
): AncestorScope | undefined => {
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return undefined;
  }
  const { name } = shape.call;
  if (name !== '=' && name !== 'in') {
    return undefined;
  }
  const params = (shape.call.params ?? []) as readonly AstInput[];
  if (params.length !== 2) {
    return undefined;
  }
  const labels = rowColLabels(params[0] as AstInput);
  if (labels === undefined || labels.length !== 2 || labels[1] !== 'externalId') {
    return undefined;
  }
  const relation = labels[0] as AncestorRelation;
  if (!relations.includes(relation)) {
    return undefined;
  }
  const externalIds = membershipExternalIds(params[1] as AstInput);
  return externalIds === undefined ? undefined : { relation, externalIds };
};

/** True iff any leaf in `node`'s tree is an ancestor-scope leaf. */
const hasAncestorScopeLeaf = (node: AstInput, relations: readonly AncestorRelation[]): boolean => {
  if (isAncestorScopeLeaf(node, relations) !== undefined) {
    return true;
  }
  const shape = astShape(node);
  if (shape.kind !== 'opCall') {
    return false;
  }
  return (shape.call.params ?? []).some((p: AstInput) => hasAncestorScopeLeaf(p, relations));
};

/** The output of the pushdown `translate`: the source-native remainder + ancestor scopes. */
export type PushdownTranslation = {
  /** The source-native predicate remainder (canonical→source renamed), if any. */
  readonly filter?: AstInput;
  /** The ancestor scopes carried on the native blob for fetch-time resolution. */
  readonly ancestorScopes: readonly AncestorScope[];
};

/**
 * Resolve the carried ancestor scopes into a single source-space predicate to
 * AND into the source query, or `undefined` when there are no scopes.
 *
 * Per scope:
 *   - relation is source-resident (FlatHierarchy/SectorAllocation): filter the
 *     source's own relation column directly.
 *   - otherwise: reverse-query FlatHierarchy for the self-grain refs under that
 *     ancestor (memoized per session), then filter the source's driving-ref
 *     column to that set. Enforces `SET_REF_MAX` on the resolved id count.
 */
export const resolveAncestorScopeFilter = async (args: {
  readonly scopes: readonly AncestorScope[];
  readonly source: DataModelView;
  readonly cols: readonly Resolved[];
  readonly enrich: ScopeEnrich | undefined;
  readonly client: CogniteClient;
  readonly mintSeries: MintSeriesSource;
  readonly ctx: SourceFetcherCtx;
}): Promise<AstInput | undefined> => {
  const { scopes, source, cols, enrich, client, mintSeries, ctx } = args;
  if (scopes.length === 0) {
    return undefined;
  }
  const sourceSchema = viewToSchema(source);
  // The FlatHierarchy column the view's own rows match: the producing string by
  // default, or a coarser ancestor relation (e.g. `well`) for an entity view at
  // that grain. The semi-join reverse-resolves the scope to refs at this grain.
  const selfColumn = enrich?.selfColumn ?? STRING_REL_COLUMN;

  const clauses = await Promise.all(
    scopes.map(async (scope: AncestorScope): Promise<AstInput> => {
      // Source-resident relation: filter the source's own column directly.
      if (sourceSchema.has(scope.relation)) {
        return op('in', colRef(scope.relation, 'externalId'), scope.externalIds as readonly AstInput[]);
      }

      // Semi-join: reverse-query FlatHierarchy for the self-grain refs under
      // this ancestor, memoized per session by the self column + sorted scope set.
      const sortedIds = [...scope.externalIds].sort();
      const selfIds = await ctx.session.fetch<readonly string[]>(
        ['virtual-view:ancestor-scope', FlatHierarchyAgentView.externalId, scope.relation, selfColumn, sortedIds],
        async (): Promise<readonly string[]> => {
          const payload = buildCdfViewQuery(
            {
              view: FlatHierarchyAgentView.externalId,
              filter: op('in', colRef(scope.relation, 'externalId'), scope.externalIds as readonly AstInput[]),
              columns: [selfColumn],
            },
            FlatHierarchyAgentView,
          );
          const rows = await FlatHierarchyAgentView.query(client, payload);
          const projected = instancesToColumns(
            rows,
            viewToSchema(FlatHierarchyAgentView),
            FlatHierarchyAgentView,
            mintSeries,
          );
          const selfCells: readonly Cell[] = projected[selfColumn] ?? [];
          const externalIds = selfCells.flatMap((cell: Cell): string[] => {
            const selfRef = asRef(cell);
            return selfRef === null ? [] : [selfRef.externalId];
          });
          return [...new Set(externalIds)];
        },
      );

      if (selfIds.length > SET_REF_MAX) {
        throw new SetRefTooLargeError(selfIds.length, SET_REF_MAX, NO_POS);
      }

      // Build the clause on the source's driving-ref column.
      if (enrich?.from === 'self') {
        return op('in', colRef('externalId'), selfIds as readonly AstInput[]);
      }
      const refCol = cols.find((c: Resolved) => c.canonical === enrich?.stringRefColumn);
      if (refCol === undefined) {
        throw new Error(
          `ancestor-scope pushdown: no kept column matches the producing-string ref '${enrich?.stringRefColumn}'`,
        );
      }
      return op('in', colRef((refCol as ResolvedFlat).source, 'externalId'), selfIds as readonly AstInput[]);
    }),
  );

  return andClauses(clauses);
};

/**
 * The native blob an ancestor-scoped provider absorbs: the source view +
 * its column projection + the source filter (fixed `where` AND any pushed
 * source-native predicate) + the pushed ancestor scopes (resolved to a
 * source filter at fetch time). JSON-stable (raw AST + scalars), so it
 * keys the source cache cleanly — `CdfViewNative` plus `ancestorScopes`.
 */
export type AncestorScopedNative = CdfViewNative & { readonly ancestorScopes?: readonly AncestorScope[] };

/**
 * Agent docs for the ancestor-struct columns: one relation `ColumnDoc` per
 * requested relation, each a `{ space, externalId }` struct whose
 * `referencedType` names the related view. Shared verbatim by the enriched/
 * flattened factories so the wording is a one-edit fix.
 */
export const describeAncestorColumns = (relations: readonly AncestorRelation[]): ColumnDoc[] =>
  relations.map(
    (rel: AncestorRelation): ColumnDoc => ({
      name: rel,
      type: ancestorStruct,
      referencedType: rel,
      description: `Ancestor ${rel} of this row's producing string, as a { space, externalId, name } reference (name resolved from the entity registry; null when unknown).`,
    }),
  );

/** The scoped inputs the shared fetch HEAD hands to `assemble`: the carried
 * native blob, the scoped source rows already read, and the fetch ctx. */
export type AncestorScopedAssembleArgs = {
  readonly native: AncestorScopedNative;
  readonly rows: NodeDefinition[];
  readonly ctx: SourceFetcherCtx;
};

/**
 * The fetch-time assembly step a custom-fetch builder owns: given the scoped
 * source rows already read at the shared fetch HEAD, project + assemble the
 * builder's surface (keep-flat, assemble-struct, explode-long, or align-wide)
 * into the materialized dataset. The single point all three ancestor-scoped
 * builders diverge.
 */
export type AncestorScopedAssemble = (args: AncestorScopedAssembleArgs) => Promise<MaterializedDataset>;

/**
 * The shared ancestor-scoped custom-fetch provider: the scaffold + returned
 * provider object + fetch HEAD every enriched/flattened/aligned builder repeats
 * verbatim. Owns the pushdown hooks, the provider shell, the eager plan, the
 * value-collapse rule, and the fetch HEAD (the `as AncestorScopedNative` cast
 * and the scoped source read), then delegates the per-builder projection to
 * `assemble`. A builder reduces to declaring its schema + docs and supplying an
 * `assemble` closure. `where` is the optional fixed predicate folded into the
 * source filter at plan time (omitted reproduces a leaf with no fixed predicate).
 */
export const makeAncestorScopedProvider = (args: {
  readonly name: string;
  readonly declared: Schema;
  readonly viewDoc: ViewDescription;
  readonly source: DataModelView;
  readonly cols: readonly Resolved[];
  readonly sourceColumns: readonly string[];
  readonly relations: readonly AncestorRelation[];
  readonly enrich: ScopeEnrich | undefined;
  readonly client: CogniteClient;
  readonly mintSeries: MintSeriesSource;
  readonly where?: AstInput;
  readonly assemble: AncestorScopedAssemble;
}): VirtualView => {
  const {
    name,
    declared,
    viewDoc,
    source,
    cols,
    sourceColumns,
    relations,
    enrich,
    client,
    mintSeries,
    where,
    assemble,
  } = args;

  // Pushdown hooks: partition the agent predicate — ancestor scopes and pure
  // source-native leaves push to CDF (scopes ride `native` as data, resolved at
  // fetch time), everything else replays as a residual. `or`/`not` wrapping an
  // ancestor leaf stay residual (semi-join scopes only compose under AND).
  const sourceSchema = viewToSchema(source);
  const isPushable = (node: AstInput): boolean => {
    if (!hasAncestorScopeLeaf(node, relations)) {
      return isPushableNode(renameCanonicalToSource(node, cols), sourceSchema, source);
    }
    if (isAncestorScopeLeaf(node, relations) !== undefined) {
      return true;
    }
    if (isAndCall(node)) {
      return andChildren(node).every((child: AstInput) => isPushable(child));
    }
    return false;
  };
  const translate = (pushed: AstInput): PushdownTranslation => {
    const leaves = andChildren(pushed);
    const ancestorScopes = leaves.flatMap((leaf: AstInput): AncestorScope[] => {
      const scope = isAncestorScopeLeaf(leaf, relations);
      return scope === undefined ? [] : [scope];
    });
    const sourceLeaves = leaves.filter((leaf: AstInput) => isAncestorScopeLeaf(leaf, relations) === undefined);
    const filter = andClauses(sourceLeaves.map((leaf: AstInput) => renameCanonicalToSource(leaf, cols)));
    return { ...(filter === undefined ? {} : { filter }), ancestorScopes };
  };
  const hooks: PipelineHooks<PushdownTranslation> = { isPushable, translate, absorbableConstraints: [] };

  const shell = makeVirtualViewProviderShell({ name, schema: declared, viewDoc });

  return {
    name,
    provider: {
      ...shell,

      // A direct-relation LHS compares against the target node's identity, so a
      // dataset-valued RHS collapses to its `externalId` set before partition.
      collapseRef: (_lhsCol: string, rhs: MaterializedDataset): AstInput =>
        [...new Set(rhs.columns.externalId ?? [])] as readonly AstInput[],

      // Eager plan: the pushdown hooks partition the predicate; ancestor scopes +
      // the source filter (fixed `where` AND any pushed source-native leaf) ride
      // `native`, the rest replays as a residual. Declared == fetched.
      plan: (_name: string, params: DecodedParams, _ctx: PlanCtx) => {
        const parsed = parseSourcePipeline(params, declared, hooks);
        const pushed = parsed.native;
        const ancestorScopes: readonly AncestorScope[] = pushed?.ancestorScopes ?? [];
        const sourceFilter = andFilters(where, pushed?.filter);
        const native: AncestorScopedNative = {
          view: source.externalId,
          columns: sourceColumns,
          ...(sourceFilter === undefined ? {} : { filter: sourceFilter }),
          ...(ancestorScopes.length === 0 ? {} : { ancestorScopes }),
        };
        const providerSource: ProviderSource = { kind: 'provider', providerId: shell.id, native };
        return {
          source: providerSource,
          residual: parsed.residualOps,
          schema: projectSchema(declared, params.columns),
        };
      },

      // Fetch HEAD: resolve the carried ancestor scopes into a source-space
      // predicate, AND it into the source filter (the fixed `where` is already
      // folded in at plan time), read the SCOPED source rows, then delegate to
      // the per-builder assembly.
      fetch: async (providerSource: ProviderSource, ctx: SourceFetcherCtx): Promise<MaterializedDataset> => {
        const native = providerSource.native as AncestorScopedNative;
        const scopeFilter = await resolveAncestorScopeFilter({
          scopes: native.ancestorScopes ?? [],
          source,
          cols,
          enrich,
          client,
          mintSeries,
          ctx,
        });
        const scopedNative: AncestorScopedNative = { ...native, filter: andFilters(native.filter, scopeFilter) };
        const rows = await source.query(client, buildCdfViewQuery(scopedNative, source));
        return assemble({ native, rows, ctx });
      },
    } satisfies DatasetProvider<DecodedParams, AncestorScopedNative>,
  };
};
